import { rgba } from 'polished';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { tokens } from '../../theme/tokens';
import { Delta } from '../Delta';
import { Skeleton } from '../Skeleton';
import { Paragraph } from '../Text';

const DEFAULT_CIRCLE_SIZE = 50;
const DEFAULT_CIRCLE_SCALE = 1.6;
const DEFAULT_NORM_TOLERANCE = 2;
const MAX_NORM_CIRCLE_SIZE = 80;

// How far a cell sits from its norm, in percentage points.
type NormSeverity = 'neutral' | 'warning' | 'danger';

// Both axis labels live in the same grid as the cells, so they stay centered on
// the cells instead of on the component as a whole.
const Layout = styled.div<{ $hasYAxisLabel: boolean }>`
	display: grid;
	grid-template-columns: ${(props) =>
		props.$hasYAxisLabel ? 'auto minmax(0, 1fr)' : 'minmax(0, 1fr)'};
	width: 100%;
`;

// The cells divide the available width equally, so the borders keep falling on
// the column boundaries no matter how wide the grid is.
const Grid = styled.div<{ $columns: number }>`
	display: grid;
	grid-template-columns: repeat(${(props) => props.$columns}, minmax(0, 1fr));
	width: 100%;
`;

const XAxisLabel = styled.div<{ $column: number }>`
	grid-column-start: ${(props) => props.$column};
	margin-top: 16px;
`;

const YAxisLabel = styled.div`
	position: relative;
	align-self: stretch;
	width: 18px;
	margin-right: 16px;
`;

const YAxisText = styled(Paragraph)`
	position: absolute;
	top: 50%;
	left: 50%;
	margin: 0;
	white-space: nowrap;
	transform: translate(-50%, -50%) rotate(-90deg);
`;

const Cell = styled(Flex)<{ $lastColumn: boolean; $lastRow: boolean }>`
	min-height: 180px;
	width: 100%;
	padding: 16px 8px;
	${(props) => !props.$lastColumn && `border-right: 1px solid ${tokens.border.subtle};`}
	${(props) => !props.$lastRow && `border-bottom: 1px solid ${tokens.border.subtle};`}
`;

const CircleContainer = styled(Flex)<{ $size: number }>`
	align-items: center;
	justify-content: center;
	width: ${(props) => props.$size}px;
	height: ${(props) => props.$size}px;
`;

const Circle = styled.div<{ $size: number }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${(props) => props.$size}px;
	height: ${(props) => props.$size}px;
	border-radius: 50%;
	background-color: ${tokens.surface.secondary};
	border: 1px solid ${tokens.border.subtle};

	p {
		font-size: 17px;
	}
`;

// The value and the norm are drawn as two concentric circles, so their
// diameters can be compared at a glance.
const CircleStack = styled.div<{ $size: number }>`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${(props) => props.$size}px;
	height: ${(props) => props.$size}px;

	p {
		font-size: 17px;
	}
`;

const ValueCircle = styled.div<{ $size: number; $severity: NormSeverity }>`
	position: absolute;
	width: ${(props) => props.$size}px;
	height: ${(props) => props.$size}px;
	border-radius: 50%;
	background-color: ${(props) =>
		props.$severity === 'danger'
			? rgba(props.theme.colors.red, 0.12)
			: props.$severity === 'warning'
				? rgba(props.theme.colors.warning, 0.18)
				: tokens.surface.secondary};
`;

const NormCircle = styled.div<{ $size: number }>`
	position: absolute;
	width: ${(props) => props.$size}px;
	height: ${(props) => props.$size}px;
	border-radius: 50%;
	border: 1px dashed ${tokens.text.secondary};
`;

const SEVERITY_COLOR: Record<NormSeverity, string> = {
	neutral: 'slateBlue',
	warning: 'warning',
	danger: 'red',
};

function severityFor(deviation: number, tolerance: number): NormSeverity {
	const distance = Math.abs(deviation);

	if (distance <= tolerance) return 'neutral';
	if (distance <= tolerance * 2) return 'warning';

	return 'danger';
}

export type TalentReviewGridCell = {
	title: string;
	// Share of the population in this cell, as a percentage (0-100).
	value: number;
	// The same share in the previous period, used to render the delta badge.
	previousValue?: number;
	// The share this cell should hold according to the norm distribution.
	norm?: number;
	deltaTooltip?: ReactNode;
};

type TalentReviewGridProps = {
	// Rows are rendered in the order given: the first row is the top row.
	rows: TalentReviewGridCell[][];
	/**
	 * `delta` compares every cell to the previous period, `norm` compares it to
	 * the norm distribution of the template.
	 */
	variant?: 'delta' | 'norm';
	xAxisLabel?: string;
	yAxisLabel?: string;
	// Diameter of a 0% circle, in pixels.
	circleSize?: number;
	/**
	 * Pixels added to the diameter per percentage point, in the norm variant.
	 * Lowered automatically when the largest circle would pass 80px.
	 */
	circleScale?: number;
	// Deviation, in percentage points, a cell may have before it is flagged.
	normTolerance?: number;
};

function NormCell({
	cell,
	circleSize,
	circleScale,
	normTolerance,
	slotSize,
}: {
	cell: TalentReviewGridCell;
	circleSize: number;
	circleScale: number;
	normTolerance: number;
	// Diameter of the largest circle in the grid, so every row lines up.
	slotSize: number;
}) {
	const { t } = useTranslation();

	const norm = cell.norm ?? 0;
	const displayedValue = Math.round(cell.value);
	const displayedNorm = Math.round(norm);
	const deviation = displayedValue - displayedNorm;
	const severity = severityFor(deviation, normTolerance);

	const valueDiameter = circleSize + cell.value * circleScale;
	const normDiameter = circleSize + norm * circleScale;

	return (
		<>
			<CircleStack $size={slotSize}>
				<ValueCircle $size={valueDiameter} $severity={severity} />
				<NormCircle $size={normDiameter} />
				<Paragraph fontWeight="bold" color="antracite" style={{ position: 'relative' }}>
					{displayedValue}%
				</Paragraph>
			</CircleStack>
			<Paragraph small color="slateBlue" textAlign="center" mt={3}>
				{t('analytics.talentReview.grid.norm', { value: displayedNorm })}
			</Paragraph>
			<Paragraph small color={SEVERITY_COLOR[severity]} textAlign="center">
				{deviation === 0
					? t('analytics.talentReview.grid.onNorm')
					: t('analytics.talentReview.grid.deviation', { value: Math.abs(deviation) })}
			</Paragraph>
		</>
	);
}

export function TalentReviewGrid({
	rows,
	variant = 'delta',
	xAxisLabel,
	yAxisLabel,
	circleSize = DEFAULT_CIRCLE_SIZE,
	circleScale = DEFAULT_CIRCLE_SCALE,
	normTolerance = DEFAULT_NORM_TOLERANCE,
}: TalentReviewGridProps) {
	const columns = Math.max(...rows.map((row) => row.length), 1);
	const largestShare = Math.max(
		...rows.flatMap((row) => row.flatMap((cell) => [cell.value, cell.norm ?? 0])),
		0,
	);

	// The largest circle may not grow past MAX_NORM_CIRCLE_SIZE, so the scale
	// shrinks along with it and every circle keeps its relative diameter.
	const normBaseSize = Math.min(circleSize, MAX_NORM_CIRCLE_SIZE);
	const largestDiameter = normBaseSize + largestShare * circleScale;
	const normScale =
		largestDiameter > MAX_NORM_CIRCLE_SIZE && largestShare > 0
			? (MAX_NORM_CIRCLE_SIZE - normBaseSize) / largestShare
			: circleScale;

	return (
		<Layout $hasYAxisLabel={Boolean(yAxisLabel)}>
			{yAxisLabel && (
				<YAxisLabel>
					<YAxisText small color="slateBlue">
						{yAxisLabel}
					</YAxisText>
				</YAxisLabel>
			)}
			<Grid $columns={columns}>
				{rows.map((row, rowIndex) =>
					row.map((cell, columnIndex) => (
						<Cell
							flexDirection="column"
							alignItems="center"
							justifyContent="center"
							key={`${rowIndex}-${columnIndex}`}
							$lastColumn={columnIndex === row.length - 1}
							$lastRow={rowIndex === rows.length - 1}
						>
							<Paragraph
								color="antracite"
								fontWeight="bold"
								fontSize="14px"
								textAlign="center"
								mb={3}
							>
								{cell.title}
							</Paragraph>
							{variant === 'norm' ? (
								<NormCell
									cell={cell}
									circleSize={normBaseSize}
									circleScale={normScale}
									normTolerance={normTolerance}
									slotSize={Math.min(largestDiameter, MAX_NORM_CIRCLE_SIZE)}
								/>
							) : (
								<>
									<CircleContainer $size={circleSize}>
										<Circle $size={circleSize}>
											<Paragraph fontWeight="bold" color="antracite">
												{Math.round(cell.value)}%
											</Paragraph>
										</Circle>
									</CircleContainer>
									{cell.previousValue !== undefined && (
										<Box mt={3}>
											<Delta
												unit="percentagePoint"
												currentValue={cell.value}
												previousValue={cell.previousValue}
												tooltip={cell.deltaTooltip}
											/>
										</Box>
									)}
								</>
							)}
						</Cell>
					)),
				)}
			</Grid>
			{xAxisLabel && (
				<XAxisLabel $column={yAxisLabel ? 2 : 1}>
					<Paragraph small color="slateBlue" textAlign="center">
						{xAxisLabel}
					</Paragraph>
				</XAxisLabel>
			)}
		</Layout>
	);
}

const CircleSkeleton = styled(Skeleton.Box)`
	width: ${DEFAULT_CIRCLE_SIZE}px;
	height: ${DEFAULT_CIRCLE_SIZE}px;
	border-radius: 50%;
	flex-shrink: 0;
`;

function TalentReviewGridSkeleton({ rows = 3, columns = 3 }: { rows?: number; columns?: number }) {
	return (
		<Layout $hasYAxisLabel>
			<YAxisLabel>
				<Paragraph.Skeleton lines={1} width={72} />
			</YAxisLabel>
			<Grid $columns={columns}>
				{Array.from({ length: rows }, (_, rowIndex) =>
					Array.from({ length: columns }, (_, columnIndex) => (
						<Cell
							flexDirection="column"
							alignItems="center"
							justifyContent="center"
							key={`${rowIndex}-${columnIndex}`}
							$lastColumn={columnIndex === columns - 1}
							$lastRow={rowIndex === rows - 1}
						>
							<Paragraph.Skeleton lines={1} width={72} mb={3} />
							<CircleSkeleton />
							<Paragraph.Skeleton lines={1} width={40} mt={3} />
						</Cell>
					)),
				)}
			</Grid>
			<XAxisLabel $column={2}>
				<Paragraph.Skeleton lines={1} width={88} mx="auto" />
			</XAxisLabel>
		</Layout>
	);
}

TalentReviewGrid.Skeleton = TalentReviewGridSkeleton;
